import type { JSX } from "preact";
import { cumulativeWithCola } from "../engine";
import { fmtMoney } from "./format";

interface ChartProps {
  pia: number;
  /** FRA in decimal years (e.g., 66.1667 for 66y2mo). */
  fraAgeYears: number;
  colaRate: number;
  longevityAge: number;
  customClaimAge: number;
}

interface Series {
  label: string;
  color: string;
  claimAge: number;
}

const CHART_W = 640;
const CHART_H = 320;
const PAD_L = 64;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 40;

/**
 * Local reimplementation of the engine's applyClaimAdjustment math.
 * Kept inline only to avoid passing the full PersonInputs through the chart.
 * Mirrors src/engine/claim-adjustment.ts — change them together.
 */
function pctOfPia(claimAgeYears: number, fraAgeYears: number): number {
  const claimMonths = Math.round(claimAgeYears * 12);
  const fraMonths = Math.round(fraAgeYears * 12);
  const delta = claimMonths - fraMonths;
  if (delta === 0) return 1.0;
  if (delta < 0) {
    const monthsEarly = -delta;
    if (monthsEarly <= 36) return 1 - monthsEarly * (5 / 9) * 0.01;
    return 1 - 36 * (5 / 9) * 0.01 - (monthsEarly - 36) * (5 / 12) * 0.01;
  }
  const monthsLate = Math.min(delta, 70 * 12 - fraMonths);
  return 1 + monthsLate * (2 / 3) * 0.01;
}

export function BreakEvenChart({
  pia,
  fraAgeYears,
  colaRate,
  longevityAge,
  customClaimAge,
}: ChartProps): JSX.Element {
  const ageStart = 62;
  const ageEnd = Math.max(longevityAge, 90);

  const ages: number[] = [];
  for (let a = ageStart; a <= ageEnd; a += 0.5) ages.push(a);

  const fraLabel =
    Number.isInteger(fraAgeYears)
      ? `${fraAgeYears}`
      : fraAgeYears.toFixed(2);
  const allSeries: Series[] = [
    { label: "Claim at 62", color: "#c45a3a", claimAge: 62 },
    { label: `Claim at FRA (${fraLabel})`, color: "#3a6ea5", claimAge: fraAgeYears },
    { label: "Claim at 70", color: "#2f7d3e", claimAge: 70 },
  ];
  if (
    customClaimAge !== 62 &&
    Math.abs(customClaimAge - fraAgeYears) > 0.001 &&
    customClaimAge !== 70
  ) {
    allSeries.push({
      label: `Claim at ${customClaimAge}`,
      color: "#7a4ea3",
      claimAge: customClaimAge,
    });
  }

  const seriesData = allSeries.map((s) => {
    const initial = pia * pctOfPia(s.claimAge, fraAgeYears);
    const claimMonthsExact = Math.round(s.claimAge * 12);
    const points = ages.map((age) => {
      if (age < s.claimAge) return { age, value: 0 };
      const value = cumulativeWithCola({
        monthlyBenefitInitial: initial,
        claimAgeMonths: claimMonthsExact,
        longevityAgeMonths: Math.round(age * 12),
        colaRate,
      });
      return { age, value };
    });
    return { series: s, points };
  });

  const maxY = Math.max(
    1,
    ...seriesData.flatMap((sd) => sd.points.map((p) => p.value))
  );
  const yTickStep = niceStep(maxY / 4);
  const yMax = Math.ceil(maxY / yTickStep) * yTickStep;

  const x = (age: number) =>
    PAD_L + ((age - ageStart) / (ageEnd - ageStart)) * (CHART_W - PAD_L - PAD_R);
  const y = (val: number) =>
    CHART_H - PAD_B - (val / yMax) * (CHART_H - PAD_T - PAD_B);

  const pathFor = (points: { age: number; value: number }[]) =>
    points
      .map(
        (p, i) =>
          `${i === 0 ? "M" : "L"} ${x(p.age).toFixed(1)} ${y(p.value).toFixed(1)}`
      )
      .join(" ");

  const xTicks: number[] = [];
  for (let a = ageStart; a <= ageEnd; a += 5) xTicks.push(a);
  if (xTicks[xTicks.length - 1] !== ageEnd) xTicks.push(ageEnd);

  const yTicks: number[] = [];
  for (let v = 0; v <= yMax; v += yTickStep) yTicks.push(v);

  return (
    <figure class="tca-ss-calc__chart">
      <figcaption class="tca-ss-calc__chart-caption">
        Cumulative lifetime benefit by claim age, with{" "}
        {(colaRate * 100).toFixed(1)}% COLA, through age {longevityAge}
      </figcaption>
      <svg
        class="tca-ss-calc__chart-svg"
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        role="img"
        aria-label="Break-even chart comparing cumulative lifetime Social Security benefits across claim ages"
        preserveAspectRatio="xMidYMid meet"
      >
        {yTicks.map((v) => (
          <g class="tca-ss-calc__chart-grid">
            <line
              x1={PAD_L}
              x2={CHART_W - PAD_R}
              y1={y(v)}
              y2={y(v)}
              stroke="#e6e2da"
              stroke-width="1"
            />
            <text
              x={PAD_L - 8}
              y={y(v) + 4}
              text-anchor="end"
              font-size="11"
              fill="#5a544a"
            >
              {fmtMoney(v)}
            </text>
          </g>
        ))}
        {xTicks.map((a) => (
          <g>
            <line
              x1={x(a)}
              x2={x(a)}
              y1={CHART_H - PAD_B}
              y2={CHART_H - PAD_B + 4}
              stroke="#5a544a"
            />
            <text
              x={x(a)}
              y={CHART_H - PAD_B + 18}
              text-anchor="middle"
              font-size="11"
              fill="#5a544a"
            >
              {a}
            </text>
          </g>
        ))}
        <line
          x1={x(longevityAge)}
          x2={x(longevityAge)}
          y1={PAD_T}
          y2={CHART_H - PAD_B}
          stroke="#8a8275"
          stroke-dasharray="4 3"
          stroke-width="1"
        />
        <text
          x={x(longevityAge)}
          y={PAD_T + 12}
          text-anchor="middle"
          font-size="10"
          fill="#8a8275"
        >
          Longevity {longevityAge}
        </text>
        {seriesData.map((sd) => (
          <path
            d={pathFor(sd.points)}
            fill="none"
            stroke={sd.series.color}
            stroke-width="2.5"
            stroke-linejoin="round"
            stroke-linecap="round"
          />
        ))}
        <line
          x1={PAD_L}
          x2={PAD_L}
          y1={PAD_T}
          y2={CHART_H - PAD_B}
          stroke="#5a544a"
        />
        <line
          x1={PAD_L}
          x2={CHART_W - PAD_R}
          y1={CHART_H - PAD_B}
          y2={CHART_H - PAD_B}
          stroke="#5a544a"
        />
      </svg>
      <ul class="tca-ss-calc__chart-legend">
        {seriesData.map((sd) => (
          <li>
            <span
              class="tca-ss-calc__chart-swatch"
              style={`background:${sd.series.color}`}
            />
            {sd.series.label}
          </li>
        ))}
      </ul>
    </figure>
  );
}

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let nice: number;
  if (norm < 1.5) nice = 1;
  else if (norm < 3) nice = 2;
  else if (norm < 7) nice = 5;
  else nice = 10;
  return nice * mag;
}
